/**
 * Pure helpers extracted from Editor.jsx (V2.0.61 monolith split).
 *
 * All functions in this module are side-effect-free and dependency-free
 * (no React, no DOM, no fetch). Anything that needs UI state stays in
 * Editor.jsx. This file is unit-testable in isolation.
 *
 * Exports:
 *  - advanceBuildQueue(q)        Mark active phase completed, promote next.
 *  - completeFinalPhase(q)       Final chat turn — complete the active phase.
 *  - countBlueprintRooms(bp)     Total room count across all floors.
 *  - diffFixtures(before, after) Fixture-level diff between two blueprints.
 *  - looksLikeDeleteIntent(text) Heuristic destructive-verb sniffer.
 *  - buildTourWaypoints(bp)      4-waypoint cinematic tour from a blueprint.
 */

// V1.5.3 Build Plan helpers — pure queue cursor movements. The first
// in_progress phase is treated as the "active" cursor; advancing means
// marking it completed and promoting the next pending phase to in_progress.
export const advanceBuildQueue = (q) => {
  if (!Array.isArray(q) || q.length === 0) return q;
  let activeIdx = q.findIndex((p) => p?.status === "in_progress");
  if (activeIdx === -1) {
    activeIdx = q.findIndex((p) => p?.status === "pending");
    if (activeIdx === -1) return q;
  }
  const nextIdx = q.findIndex(
    (p, i) => i > activeIdx && p?.status === "pending",
  );
  return q.map((p, i) => {
    if (i === activeIdx) return { ...p, status: "completed" };
    if (i === nextIdx) return { ...p, status: "in_progress" };
    return p;
  });
};

// On the FINAL turn of a chat batch we finish whatever phase was in_progress.
export const completeFinalPhase = (q) => {
  if (!Array.isArray(q) || q.length === 0) return q;
  const activeIdx = q.findIndex((p) => p?.status === "in_progress");
  if (activeIdx === -1) return q;
  return q.map((p, i) =>
    i === activeIdx ? { ...p, status: "completed" } : p,
  );
};

// Count rooms across all floors of a simple-mode blueprint. Used to detect
// when a chat round actually deleted rooms (vs purely modified them) so
// the editor can show a clearer "Removed N rooms — Undo" toast.
export const countBlueprintRooms = (bp) => {
  if (!bp || !Array.isArray(bp.floors)) return 0;
  return bp.floors.reduce(
    (acc, f) => acc + (Array.isArray(f?.rooms) ? f.rooms.length : 0),
    0,
  );
};

// V1.8.5 — Diff helper for fixtures across a chat-turn. Returns flat lists
// of `type` strings for fixtures that appeared / disappeared between
// `before` and `after` blueprints. Used for the smart toast wording AND
// the `fixture.added` analytics event.
export const diffFixtures = (before, after) => {
  const collect = (bp) => {
    const arr = [];
    if (!bp || !Array.isArray(bp.floors)) return arr;
    bp.floors.forEach((f) => {
      (f?.fixtures || []).forEach((fx) => {
        if (!fx) return;
        arr.push({
          id: fx.id || `${fx.type}_${fx.x}_${fx.z}`,
          type: String(fx.type || "").toLowerCase(),
        });
      });
    });
    return arr;
  };
  const beforeMap = new Map(collect(before).map((f) => [f.id, f.type]));
  const afterMap = new Map(collect(after).map((f) => [f.id, f.type]));
  const added = [];
  const removed = [];
  afterMap.forEach((type, id) => {
    if (!beforeMap.has(id)) added.push(type);
  });
  beforeMap.forEach((type, id) => {
    if (!afterMap.has(id)) removed.push(type);
  });
  return { added, removed };
};

// Lightweight intent check: did the user's message contain a destructive
// verb? Used as a secondary signal to upgrade the toast wording even when
// only a single room is removed (which could also be a rename).
export const looksLikeDeleteIntent = (text) =>
  /\b(remove|removed|delete|deleted|drop|tear down|take out|get rid of)\b/i.test(text || "");

// V2.0.85 — Merge a parsed blueprint (from the upload-source vision
// pipeline) into an existing blueprint. Non-destructive:
//   - Existing rooms/walls/doors/windows are preserved as-is.
//   - Parsed rooms are shifted horizontally by (maxX + 5m) so they
//     don't overlap the existing plan visually.
//   - Parsed room IDs are re-namespaced with a `p_` prefix to guarantee
//     no collision with existing ids. Doors/windows that reference the
//     old room/wall ids also get updated.
// V2.0.91 — When the parsed blueprint targets a different storey
// (floor_index > 0, e.g. "this file is Floor 2"), we merge into that
// floor index WITHOUT the horizontal shift — it's a whole new storey,
// not a side wing.
// The result is safe to pass through the backend's repair_blueprint
// pass on save — orphan opening refs get dropped there anyway.
export const mergeParsedBlueprint = (existing, parsed) => {
  const safeExisting = existing && typeof existing === "object" ? existing : { floors: [], materials: {} };
  const safeParsed = parsed && typeof parsed === "object" ? parsed : { floors: [], materials: {} };
  const existingFloors = Array.isArray(safeExisting.floors) ? safeExisting.floors.slice() : [];
  const parsedFloors = Array.isArray(safeParsed.floors) ? safeParsed.floors : [];
  if (parsedFloors.length === 0) return safeExisting;

  const parsedGround = parsedFloors[0] || { rooms: [], doors: [], windows: [], walls: [] };
  const targetFloorIndex = Number.isInteger(parsedGround?.floor_index) ? parsedGround.floor_index : 0;
  const isDifferentStorey = targetFloorIndex > 0 && targetFloorIndex !== 0;

  // Find (or create) the target floor slot in the existing plan.
  let targetIdx = existingFloors.findIndex((f) => (f?.floor_index ?? 0) === targetFloorIndex);
  let targetExisting;
  if (targetIdx === -1) {
    targetExisting = { floor_index: targetFloorIndex, rooms: [], doors: [], windows: [], walls: [] };
    targetIdx = existingFloors.length;
    existingFloors.push(targetExisting);
  } else {
    targetExisting = existingFloors[targetIdx];
  }

  // Compute the right edge of the TARGET floor so parsed rooms sit to
  // the right of existing rooms on that floor. For a brand-new storey
  // (no existing rooms) offset stays 0.
  let existingMaxX = 0;
  (targetExisting.rooms || []).forEach((r) => {
    const px = Array.isArray(r?.position) ? Number(r.position[0]) || 0 : 0;
    const sw = Array.isArray(r?.size) ? Number(r.size[0]) || 0 : 0;
    if (px + sw > existingMaxX) existingMaxX = px + sw;
  });
  // If this is a fresh storey (no existing rooms) we DON'T shift — the
  // user expects the plan to land at origin, not floating off-plan.
  const offsetX = (isDifferentStorey && (targetExisting.rooms || []).length === 0)
    ? 0
    : (existingMaxX > 0 ? existingMaxX + 5 : 0);

  // Renamespace + shift parsed floor 0 rooms/doors/windows.
  const idMap = new Map();
  const shiftedRooms = (parsedGround.rooms || []).map((r, i) => {
    const oldId = r?.id || `r${i + 1}`;
    const newId = `p_${oldId}`;
    idMap.set(oldId, newId);
    const pos = Array.isArray(r?.position) ? r.position.slice() : [0, 0, 0];
    pos[0] = (Number(pos[0]) || 0) + offsetX;
    return { ...r, id: newId, position: pos };
  });
  const remapWallId = (wid) => {
    if (!wid || typeof wid !== "string") return wid;
    // Wall ids on the simple schema look like "r1_wall_south" — remap the r1 prefix.
    for (const [oldId, newId] of idMap.entries()) {
      if (wid.startsWith(`${oldId}_`)) {
        return wid.replace(`${oldId}_`, `${newId}_`);
      }
    }
    return wid;
  };
  const shiftedDoors = (parsedGround.doors || []).map((d, i) => ({
    ...d,
    id: `p_${d?.id || `d${i + 1}`}`,
    wall_id: remapWallId(d?.wall_id),
  }));
  const shiftedWindows = (parsedGround.windows || []).map((w, i) => ({
    ...w,
    id: `p_${w?.id || `w${i + 1}`}`,
    wall_id: remapWallId(w?.wall_id),
  }));
  const shiftedWalls = (parsedGround.walls || []).map((w, i) => {
    const nextId = `p_${w?.id || `w${i + 1}`}`;
    const shiftPt = (pt) => {
      if (!Array.isArray(pt)) return pt;
      const out = pt.slice();
      out[0] = (Number(out[0]) || 0) + offsetX;
      return out;
    };
    return {
      ...w,
      id: nextId,
      start: shiftPt(w?.start),
      end: shiftPt(w?.end),
    };
  });

  const mergedTarget = {
    ...targetExisting,
    floor_index: targetFloorIndex,
    rooms: [...(targetExisting.rooms || []), ...shiftedRooms],
    doors: [...(targetExisting.doors || []), ...shiftedDoors],
    windows: [...(targetExisting.windows || []), ...shiftedWindows],
    walls: [...(targetExisting.walls || []), ...shiftedWalls],
  };

  const nextFloors = existingFloors.map((f, i) => (i === targetIdx ? mergedTarget : f));

  return {
    ...safeExisting,
    floors: nextFloors,
    materials: safeExisting.materials || safeParsed.materials || {},
  };
};
//  1. Front exterior, 2. Living room, 3. Kitchen, 4. Rear exterior
//  V1.7.9 — Simple Mode convention: z=0 = front. Waypoint 1 starts at
//  minZ - 10 (in front of the house) and waypoint 4 ends at maxZ + 10
//  (behind the house).
export const buildTourWaypoints = (blueprint) => {
  if (!blueprint) return [];
  const floors = blueprint.floors || [];
  const groundFloor = floors.find((f) => (f.height_offset || 0) >= 0 && (f.height_offset || 0) < 0.5) || floors[0];
  if (!groundFloor) return [];
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  (groundFloor.walls || []).forEach((w) => {
    [w.start, w.end].forEach((p) => {
      if (!p) return;
      minX = Math.min(minX, p[0]); maxX = Math.max(maxX, p[0]);
      minZ = Math.min(minZ, p[1]); maxZ = Math.max(maxZ, p[1]);
    });
  });
  if (!isFinite(minX)) return [];
  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;
  const rooms = groundFloor.rooms || [];
  const findRoom = (kw) =>
    rooms.find((r) => String(r.name || "").toLowerCase().includes(kw));
  const living = findRoom("living");
  const kitchen = findRoom("kitchen");
  const fallbackA = living?.center || rooms[0]?.center || [cx, cz];
  const fallbackB = kitchen?.center || rooms[1]?.center || rooms[0]?.center || [cx, cz];
  const eyeY = 1.68;
  // V2.0.17 — houseGroup is mirrored on X axis so 3D matches the 2D plan.
  // Teleport waypoints are expressed in DATA coords; negate x so the
  // camera lands at the correct world position in the mirrored scene.
  return [
    { x: -cx, z: minZ - 10, eyeY: eyeY + 2, lookY: 1.5, camOffset: 0, durationMs: 5000 },
    { x: -fallbackA[0], z: fallbackA[1], eyeY, lookY: 1.2, camOffset: 3, durationMs: 5000 },
    { x: -fallbackB[0], z: fallbackB[1], eyeY, lookY: 1.2, camOffset: 3, durationMs: 5000 },
    { x: -cx, z: maxZ + 10, eyeY: eyeY + 2, lookY: 1.5, camOffset: 0, durationMs: 5000 },
  ];
};
